/**
 * UsdLedgerManagement — USD-only internal accounting dashboard (V2).
 *
 * Never uses the word "Credits". Reads /api/admin/ledger/* and writes
 * (deposit / threshold / migrate) via super_admin cookie. The site keeps
 * working at zero or negative balance — this page is purely informational.
 */
import { useCallback, useEffect, useState } from "react";
import {
  ArrowRight, DollarSign, Loader2, RefreshCcw, AlertTriangle,
  BadgeDollarSign, Sparkles, Cpu, PlayCircle, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { ledgerApi } from "@/lib/api";

const fmtUsd = (n) => `$${Number(n ?? 0).toFixed(6)}`;
const fmtInt = (n) => Number(n ?? 0).toLocaleString("en-US");


function StatTile({ testid, label, value, hint, tone = "default", Icon }) {
  const toneCls = {
    default: "bg-white border-slate-200",
    good:    "bg-emerald-50 border-emerald-200",
    warn:    "bg-amber-50 border-amber-200",
    bad:     "bg-rose-50 border-rose-200",
  }[tone];
  return (
    <div data-testid={testid}
      className={`border-2 rounded-2xl p-4 space-y-1 ${toneCls}`}>
      <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase">
        {Icon && <Icon className="w-4 h-4" />}<span>{label}</span>
      </div>
      <div className="text-2xl font-bold text-slate-900" dir="ltr">{value}</div>
      {hint && <div className="text-xs text-slate-500">{hint}</div>}
    </div>
  );
}


export function LedgerOverview({ data, onReload }) {
  const remaining = Number(data.remaining_usd || 0);
  const funded = Number(data.funded_usd || 0);
  const consumed = Number(data.total_consumed_usd || 0);
  const threshold = Number(data.low_balance_threshold_usd || 0);
  const low = !!data.low_balance;
  const pending = Number(data.pending_reconciliation || 0);

  return (
    <div className="space-y-4">
      <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-2xl text-amber-900 text-sm leading-relaxed">
        <AlertTriangle className="w-4 h-4 inline mr-1 -mt-0.5" />
        هذا رصيد محاسبي داخلي محسوب وفق التسعيرات المحددة في الموقع، ولا يمثل
        الرصيد الفعلي لدى مزود الخدمة، ولا يؤدي انخفاضه إلى إيقاف الموقع.
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile testid="ledger-funded" label="الرصيد المضاف" Icon={BadgeDollarSign}
          value={fmtUsd(funded)} hint="مجموع الإيداعات اليدوية" tone="good" />
        <StatTile testid="ledger-consumed" label="إجمالي الاستهلاك" Icon={Sparkles}
          value={fmtUsd(consumed)} hint="من استدعاءات النماذج الفعلية" />
        <StatTile testid="ledger-remaining"
          label="الرصيد المتبقي" Icon={DollarSign}
          value={fmtUsd(remaining)}
          hint={remaining < 0 ? "سالب — الاستهلاك تجاوز الإيداعات (الخدمة تعمل)" : "قيد التتبع فقط"}
          tone={remaining < 0 ? "bad" : low ? "warn" : "good"} />
        <StatTile testid="ledger-threshold" label="حد التنبيه" Icon={AlertTriangle}
          value={fmtUsd(threshold)}
          hint={low ? "أقل من الحد المحدد" : "أعلى من الحد"} tone={low ? "warn" : "default"} />
      </div>

      {pending > 0 && (
        <div data-testid="pending-reconciliation" className="p-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-xs text-slate-700">
          <b>{pending}</b> سجل استخدام معلّق للمصالحة (Tokens غير متاحة من النموذج — لم يُخصم منها شيء).
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          آخر تحديث: {data.updated_at ? new Date(data.updated_at).toLocaleString("ar") : "—"}
        </div>
        <button type="button" data-testid="ledger-reload" onClick={onReload}
          className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 text-sm font-bold px-3 py-2 rounded-lg cursor-pointer">
          <RefreshCcw className="w-4 h-4" /> تحديث
        </button>
      </div>
    </div>
  );
}


export function DepositForm({ canEdit, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  if (!canEdit) return null;
  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!(amt > 0)) { toast.error("أدخل مبلغاً أكبر من صفر"); return; }
    setSaving(true);
    try {
      const r = await ledgerApi.deposit(amt, note || null);
      toast.success(`تمت إضافة ${fmtUsd(amt)} — الرصيد الحالي ${fmtUsd(r.funded_usd)}`);
      setAmount(""); setNote("");
      onSuccess?.(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر الإيداع");
    } finally { setSaving(false); }
  };
  return (
    <form data-testid="deposit-form" onSubmit={submit}
      className="bg-white border-2 border-slate-200 rounded-2xl p-6 space-y-3">
      <h3 className="font-bold text-slate-900 flex items-center gap-2">
        <BadgeDollarSign className="w-5 h-5 text-emerald-600" /> إضافة رصيد بالدولار
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-1">
          <label className="block text-xs font-bold text-slate-500 mb-1">المبلغ (USD)</label>
          <input type="number" min="0" step="0.01" required
            data-testid="deposit-amount" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-slate-50 border-2 border-slate-200 rounded-lg px-3 py-2 font-bold" dir="ltr" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold text-slate-500 mb-1">ملاحظة (اختيارية)</label>
          <input type="text" maxLength={200}
            data-testid="deposit-note" value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full bg-slate-50 border-2 border-slate-200 rounded-lg px-3 py-2" />
        </div>
      </div>
      <div className="flex justify-end">
        <button type="submit" disabled={saving} data-testid="deposit-submit"
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2.5 rounded-lg disabled:opacity-50 cursor-pointer">
          {saving ? "…" : "إيداع"}
        </button>
      </div>
    </form>
  );
}


export function ThresholdForm({ canEdit, current, onSuccess }) {
  const [val, setVal] = useState(String(Number(current ?? 0)));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(String(Number(current ?? 0))); }, [current]);
  if (!canEdit) return null;
  const submit = async (e) => {
    e.preventDefault();
    const n = Number(val);
    if (!(n >= 0)) { toast.error("يجب أن يكون صفراً أو أكثر"); return; }
    setSaving(true);
    try {
      const r = await ledgerApi.setThreshold(n);
      toast.success("تم حفظ حد التنبيه");
      onSuccess?.(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر الحفظ");
    } finally { setSaving(false); }
  };
  return (
    <form data-testid="threshold-form" onSubmit={submit}
      className="bg-white border-2 border-slate-200 rounded-2xl p-6 space-y-3">
      <h3 className="font-bold text-slate-900 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5 text-amber-600" /> حد تنبيه انخفاض الرصيد
      </h3>
      <p className="text-xs text-slate-500">
        تنبيه فقط — لا يوقف البحث أو OCR إذا انخفض الرصيد تحته.
      </p>
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="block text-xs font-bold text-slate-500 mb-1">الحد (USD)</label>
          <input type="number" min="0" step="0.01"
            data-testid="threshold-input" value={val}
            onChange={(e) => setVal(e.target.value)}
            className="w-full bg-slate-50 border-2 border-slate-200 rounded-lg px-3 py-2 font-bold" dir="ltr" />
        </div>
        <button type="submit" disabled={saving} data-testid="threshold-submit"
          className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-4 py-2.5 rounded-lg disabled:opacity-50 cursor-pointer">
          {saving ? "…" : "حفظ"}
        </button>
      </div>
    </form>
  );
}


export function MigrationPanel({ canEdit, onSuccess }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  if (!canEdit) return null;

  const runDry = async () => {
    setBusy(true); setApplied(null);
    try {
      const r = await ledgerApi.migrate(true);
      setPreview(r);
      if (r.already_applied) toast.info("تم تطبيق الترحيل مسبقاً");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر تشغيل dry-run");
    } finally { setBusy(false); }
  };
  const runApply = async () => {
    setBusy(true);
    try {
      const r = await ledgerApi.migrate(false);
      setApplied(r);
      if (r.applied) {
        toast.success("تم تنفيذ الترحيل بنجاح (مرة واحدة).");
        onSuccess?.();
      } else {
        toast.info(`لم يتم التنفيذ: ${r.reason || "الحالة"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر التنفيذ");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="migration-panel" className="bg-white border-2 border-slate-200 rounded-2xl p-6 space-y-3">
      <h3 className="font-bold text-slate-900 flex items-center gap-2">
        <PlayCircle className="w-5 h-5 text-blue-600" /> ترحيل الأرصدة القديمة إلى USD
      </h3>
      <p className="text-xs text-slate-500 leading-relaxed">
        الترحيل يقرأ Top-ups والاستهلاك التاريخيين، ويحوّل ما يلزم بالسعر التاريخي
        1&nbsp;Credit&nbsp;=&nbsp;$0.20، ويكتب <code>usd_ledger</code> مرة واحدة فقط عبر marker.
        <b> لا يحذف</b> البيانات القديمة، ولا يُعاد حساب التكاليف التاريخية.
      </p>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy} onClick={runDry} data-testid="migrate-dry-run"
          className="bg-slate-100 hover:bg-slate-200 text-slate-900 text-sm font-bold px-3 py-2 rounded-lg cursor-pointer">
          Dry-run (معاينة فقط)
        </button>
        <button type="button" disabled={busy || !preview || preview.already_applied} onClick={runApply}
          data-testid="migrate-apply"
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-bold px-3 py-2 rounded-lg cursor-pointer">
          تنفيذ (مرة واحدة)
        </button>
      </div>
      {preview && (
        <div data-testid="migrate-preview" className="text-xs bg-slate-50 rounded-lg p-3 border border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-2" dir="ltr">
          <div><b>Funded USD:</b> {fmtUsd(preview.projected_funded_usd)}</div>
          <div><b>Consumed USD:</b> {fmtUsd(preview.projected_consumed_usd)}</div>
          <div><b>Remaining USD:</b> {fmtUsd(preview.projected_remaining_usd)}</div>
          <div><b>Applied:</b> {preview.already_applied ? "yes (marker present)" : "no"}</div>
        </div>
      )}
      {applied && (
        <div data-testid="migrate-applied" className="text-xs bg-emerald-50 rounded-lg p-3 border border-emerald-200" dir="ltr">
          Applied: <b>{String(applied.applied)}</b>
          {applied.reason && <> · reason: {applied.reason}</>}
        </div>
      )}
    </div>
  );
}


export function ProviderPanel({ provider }) {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [s, ev] = await Promise.all([
        ledgerApi.providerStats(provider),
        ledgerApi.providerEvents(provider, 20),
      ]);
      setStats(s); setEvents(ev.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر التحميل");
    } finally { setLoading(false); }
  }, [provider]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (err) return <div data-testid={`provider-${provider}-error`} className="p-4 rounded-xl bg-red-50 border-2 border-red-200 text-red-800 text-sm">{err}</div>;
  if (!stats) return null;

  return (
    <div data-testid={`provider-${provider}`} className="space-y-4">
      <div className="flex items-center gap-2">
        <Cpu className="w-5 h-5 text-slate-700" />
        <h3 className="font-bold text-slate-900 text-lg">
          {provider === "gemini" ? "Gemini" : "DeepSeek"}
        </h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile testid={`${provider}-unique-requests`} label="طلبات مميزة" value={fmtInt(stats.unique_requests)} />
        <StatTile testid={`${provider}-api-calls`} label="استدعاءات API" value={fmtInt(stats.api_calls)} />
        <StatTile testid={`${provider}-success`} label="نجاح" value={fmtInt(stats.success)} tone="good" />
        <StatTile testid={`${provider}-failed`} label="فشل" value={fmtInt(stats.failed)} tone={stats.failed > 0 ? "warn" : "default"} />
        <StatTile testid={`${provider}-input-tokens`} label="Input Tokens" value={fmtInt(stats.input_tokens)} />
        <StatTile testid={`${provider}-output-tokens`} label="Output Tokens" value={fmtInt(stats.output_tokens)} />
        <StatTile testid={`${provider}-total-tokens`} label="Total Tokens" value={fmtInt(stats.total_tokens)} />
        <StatTile testid={`${provider}-total-cost`} label="إجمالي الاستهلاك" value={fmtUsd(stats.total_consumed_usd)} tone="good" />
        <StatTile testid={`${provider}-avg-cost`} label="متوسط تكلفة الاستدعاء" value={fmtUsd(stats.avg_cost_per_call_usd)} />
      </div>
      <div className="bg-white border-2 border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50">
            <tr className="text-slate-500 uppercase text-[10px]">
              <th className="px-3 py-2 text-right">التاريخ</th>
              <th className="px-3 py-2 text-right">العملية</th>
              <th className="px-3 py-2 text-right">المصدر</th>
              <th className="px-3 py-2 text-right">Attempt</th>
              <th className="px-3 py-2 text-right">Status</th>
              <th className="px-3 py-2 text-right">Input</th>
              <th className="px-3 py-2 text-right">Output</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Cost USD</th>
            </tr>
          </thead>
          <tbody data-testid={`${provider}-events-tbody`}>
            {events.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">لا سجلات بعد</td></tr>
            )}
            {events.map((e) => (
              <tr key={e.usage_event_id} className="border-t border-slate-100">
                <td className="px-3 py-2" dir="ltr">{new Date(e.created_at).toLocaleString("ar")}</td>
                <td className="px-3 py-2">{e.operation}</td>
                <td className="px-3 py-2">{e.source}</td>
                <td className="px-3 py-2" dir="ltr">{e.attempt_no}</td>
                <td className="px-3 py-2">{e.status}</td>
                <td className="px-3 py-2" dir="ltr">{e.input_tokens ?? "—"}</td>
                <td className="px-3 py-2" dir="ltr">{e.output_tokens ?? "—"}</td>
                <td className="px-3 py-2" dir="ltr">{e.total_tokens ?? "—"}</td>
                <td className="px-3 py-2" dir="ltr">
                  {e.cost_available ? fmtUsd(e.cost_usd) : <span className="text-slate-400">بيانات Tokens غير متاحة</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export default function UsdLedgerManagement({ onBack, me }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("overview");
  const canEdit = !!me?.is_super_admin;
  const canView = canEdit || (me?.permissions || []).includes("view_analytics");

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setOverview(await ledgerApi.overview()); }
    catch (e) { setErr(e?.response?.data?.detail || "تعذّر التحميل"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="usd-ledger-panel" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b-2 border-slate-900 pb-6">
        <div>
          <div className="text-sm font-bold text-slate-500 mb-2">دوائي / لوحة الإدارة</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 flex items-center gap-2">
            <DollarSign className="w-7 h-7" />المحاسبة الداخلية (USD)
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            رصيد داخلي محسوب من استدعاءات النماذج الفعلية · مستقل عن أي منصة خارجية
          </p>
        </div>
        <button type="button" onClick={onBack} data-testid="usd-back-btn"
          className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold px-4 py-3 rounded-xl cursor-pointer">
          <ArrowRight className="w-4 h-4" /><span>العودة إلى لوحة الأدمن</span>
        </button>
      </div>

      {!canView ? (
        <div className="p-6 bg-amber-50 border-2 border-amber-200 rounded-2xl text-amber-900 text-sm">
          لا تملك صلاحية رؤية الإحصائيات. اطلب من الأدمن الرئيسي منحك صلاحية
          <b> رؤية الإحصائيات</b>.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {[
              {k: "overview", label: "نظرة عامة"},
              {k: "gemini", label: "Gemini"},
              {k: "deepseek", label: "DeepSeek"},
              ...(canEdit ? [{k: "admin", label: "إعدادات وإدارة"}] : []),
            ].map(t => (
              <button key={t.k} type="button" onClick={() => setTab(t.k)}
                data-testid={`usd-tab-${t.k}`}
                className={`px-4 py-2 rounded-lg font-bold text-sm cursor-pointer ${tab === t.k
                  ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                {t.label}
              </button>
            ))}
          </div>

          {loading && <div className="p-6 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>}
          {err && <div data-testid="ledger-err" className="p-4 rounded-2xl bg-red-50 border-2 border-red-200 text-red-800 text-sm">{err}</div>}

          {!loading && !err && overview && tab === "overview" && (
            <LedgerOverview data={overview} onReload={load} />
          )}
          {!loading && !err && tab === "gemini" && <ProviderPanel provider="gemini" />}
          {!loading && !err && tab === "deepseek" && <ProviderPanel provider="deepseek" />}
          {!loading && !err && tab === "admin" && (
            <div className="space-y-4">
              <DepositForm canEdit={canEdit} onSuccess={load} />
              <ThresholdForm canEdit={canEdit}
                current={overview?.low_balance_threshold_usd || 0} onSuccess={load} />
              <MigrationPanel canEdit={canEdit} onSuccess={load} />
              <div className="text-xs text-slate-500 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                جميع كتابات الرصيد وحد التنبيه والترحيل يقتصرها الخادم على
                <b>&nbsp;super_admin</b>&nbsp;— لا يمكن للمستخدم بصلاحية view_analytics تعديلها.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
